import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {ButtonWithIcon, Icon} from '@/components/atoms';
import {FileUploadWithModal} from '@/components/organisms';
import {ButtonIcon} from '@pepsico-ds/ui';
import {Constants} from '@/utils/constants/commonConstants';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {uploadTemplateButtonSchema} from '@/containers/PerfectStore/PerfectStoreSetUp/schemas';

const TargetTemplateUpload = ({
  formData,
  setFormData,
  templateCsv,
  downloadFileNamePrefix,
  cardTitle = 'Store Target',
  uploadModalTitle = 'Upload Target Template',
  uploadModalSubTitle = '',
  uploadFor = Constants.SalesTarget,
  isCsvValidationRequired = false,
  cardClassName = 'border-height mb-2 rounded-lg border-2 border-solid',
  uploadButtonSchema = uploadTemplateButtonSchema,
  uploadButtonLabel,
  uploadDisabled = false,
  customRules,
  onTemplateUpdate,
  salesTargetId,
  handleDownloadOverride = null,
  customUploadFile = null,
  customUploadAction = null,
  isCustomUploading = false,
  onCloseModal = null
}) => {
  const [openUpload, setOpenUpload] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isTargetMediaFileExist = useMemo(
    () =>
      formData?.targetMediaFile?.length > 0 &&
      formData?.targetMediaFile[0] !== '',
    [formData?.targetMediaFile]
  );

  const finalButtonSchema = useMemo(
    () => ({
      ...uploadButtonSchema,
      title: uploadButtonLabel || 'Upload Target Template',
      disabled: uploadDisabled || uploadButtonSchema?.disabled
    }),
    [uploadButtonSchema, uploadButtonLabel, uploadDisabled]
  );

  useEffect(() => {
    if (uploadDisabled && openUpload) {
      setOpenUpload(false);
      onCloseModal?.();
    }
  }, [uploadDisabled, openUpload, onCloseModal]);

  const handleFileDownload = useCallback(() => {
    if (handleDownloadOverride) {
      handleDownloadOverride();
      return;
    }
    const fileName = `${downloadFileNamePrefix}_${getDateWithTimeString()}`;
    downloadCSVFiles(templateCsv, fileName);
  }, [handleDownloadOverride, downloadFileNamePrefix, templateCsv]);

  const handleReopenUpload = useCallback(() => {
    if (uploadDisabled) return;
    setOpenUpload(true);
  }, [uploadDisabled]);

  const handleSetFormData = useCallback(
    (updater) => {
      setFormData((prev) => {
        const next =
          typeof updater === 'function' ? updater(prev) : {...prev, ...updater};
        if (Object.prototype.hasOwnProperty.call(next, 'storeList')) {
          return {...next, storeTargetFileUrl: next.storeList || ''};
        }
        if (!next.targetMediaFile?.length) {
          return {...next, storeTargetFileUrl: '', storeList: ''};
        }
        return next;
      });
    },
    [setFormData]
  );

  const handleDeleteFile = useCallback(() => {
    if (uploadDisabled) return;
    setOpenUpload(true);
    handleSetFormData((prev) => ({
      ...prev,
      targetMediaFile: [],
      storeList: ''
    }));
  }, [handleSetFormData, uploadDisabled]);

  return (
    <>
      <div className={cardClassName}>
        <p className='pb-4 pl-3 pt-2 text-lg font-bold'>{cardTitle}</p>
        <div className='butt-icon mx-3 mb-4 flex items-center'>
          <ButtonIcon
            icon='file_download'
            type='default'
            onClick={handleFileDownload}
            size='small'
            data-testid='download-template-btn'
          />
          {isTargetMediaFileExist ? (
            <div className='target-template-file-chip ml-2 flex !w-full min-w-0 flex-1 items-center rounded-lg px-2 py-1 text-xs'>
              <button
                type='button'
                className='flex min-w-0 flex-1 items-center'
                disabled={uploadDisabled}
                onClick={handleReopenUpload}
                data-testid='reopen-upload-modal'>
                <Icon
                  alt='file'
                  className='insert_file_icon'
                  icon='insert_drive_file'
                  size='small'
                />
                <span className='min-w-0 flex-1 truncate pl-2 text-left'>
                  {formData?.targetMediaFile?.[0]}
                </span>
              </button>
              <Icon
                alt='delete'
                className='delete_file_icon'
                icon='delete'
                size='small'
                data-testid='file-delete-icon'
                aria-disabled={uploadDisabled}
                onClick={handleDeleteFile}
              />
            </div>
          ) : (
            <div className='ml-2 min-w-0 flex-1'>
              <ButtonWithIcon
                uiSchema={finalButtonSchema}
                iconLeading='upload'
                handleButton={handleReopenUpload}
              />
            </div>
          )}
        </div>
      </div>
      {!uploadDisabled && (
        <FileUploadWithModal
          openUpload={openUpload}
          setOpenUpload={setOpenUpload}
          setFormData={handleSetFormData}
          formData={formData}
          uploadFor={uploadFor}
          fileType='csv'
          isCsvValidationRequired={isCsvValidationRequired}
          title={uploadModalTitle}
          subTitle={uploadModalSubTitle}
          customRules={customRules}
          required={true}
          uploading={isCustomUploading || uploading}
          setUploading={setUploading}
          emptyPrimaryButtonText='Upload'
          showDownloadTemplate={true}
          handleDownloadTemplate={handleFileDownload}
          onTemplateUpdate={onTemplateUpdate}
          salesTargetId={salesTargetId}
          customUploadFile={customUploadFile}
          customUploadAction={customUploadAction}
          onCloseModal={onCloseModal}
          primaryButtonDisabled={uploadDisabled}
        />
      )}
    </>
  );
};

TargetTemplateUpload.propTypes = {
  formData: PropTypes.object,
  setFormData: PropTypes.func.isRequired,
  templateCsv: PropTypes.string,
  downloadFileNamePrefix: PropTypes.string,
  cardTitle: PropTypes.string,
  uploadModalTitle: PropTypes.string,
  uploadModalSubTitle: PropTypes.string,
  uploadFor: PropTypes.string,
  isCsvValidationRequired: PropTypes.bool,
  cardClassName: PropTypes.string,
  uploadButtonSchema: PropTypes.object,
  uploadButtonLabel: PropTypes.string,
  uploadDisabled: PropTypes.bool,
  customRules: PropTypes.object,
  onTemplateUpdate: PropTypes.func,
  salesTargetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Optional: replace the built-in download (static CSV) with a custom async handler.
  handleDownloadOverride: PropTypes.func,
  // Optional: custom upload handler for SAS token flow
  customUploadFile: PropTypes.func,
  // Optional: custom upload action for blob upload + API call
  customUploadAction: PropTypes.func,
  // Optional: uploading state for custom upload flow
  isCustomUploading: PropTypes.bool,
  // Optional: callback fired when the upload modal is closed
  onCloseModal: PropTypes.func
};

export default TargetTemplateUpload;
